import type { TranslateParams } from '../i18n'
import type { SourceLabels } from './sourceNames'
import { resolveSourceLabel } from './sourceNames'

type Translate = (key: string, params?: TranslateParams) => string

export interface RunErrorDisplay {
  /** Short, translated text for the table cell. */
  text: string
  /** Full translated detail plus the original raw error, for the `title` tooltip. */
  title: string
}

// Fixed pipeline-stage metric keys the ingestion service may report under
// `partial_failure: key=value, ...` (see `ingestion/src/argus_ingestion/repository.py`,
// `_CRITICAL_PIPELINE_FAILURE_STATS`). Mapped to the `metrics.<value>One`/`<value>Other`
// i18n keys below.
const FIXED_METRIC_KEYS: Record<string, string> = {
  'pipeline/persist/errors': 'persistErrors',
  'pipeline/attachments/errors': 'attachmentErrors',
  'pipeline/attachments/persist_errors': 'attachmentPersistErrors',
  'pipeline/extract_text/errors': 'extractTextErrors',
  'pipeline/classify/quarantine_errors': 'quarantineErrors',
}

// Per-source metric suffixes (`{sourceName}/error_pages`, `{sourceName}/detail_errors`,
// see `_CRITICAL_SOURCE_FAILURE_SUFFIXES` in the same file). Matched by suffix since the
// source-name prefix varies.
const SOURCE_METRIC_SUFFIXES: Record<string, string> = {
  error_pages: 'errorPages',
  detail_errors: 'detailErrors',
}

// Bare Scrapy `finish_reason` values the ingestion service persists as-is when a crawl
// doesn't close cleanly (no `partial_failure:` prefix). Not an exhaustive Scrapy enum —
// just the reasons this codebase is known to produce; anything else falls through to
// the generic message.
const KNOWN_FINISH_REASONS: Record<string, string> = {
  shutdown: 'shutdown',
  cancelled: 'cancelled',
  closespider_timeout: 'timeout',
  closespider_itemcount: 'itemLimit',
  closespider_pagecount: 'pageLimit',
  closespider_errorcount: 'errorLimit',
  memusage_exceeded: 'memory',
  unknown: 'unknown',
}

function metricText(t: Translate, name: string, count: number, source?: string): string {
  const key = `errors.ingestionRun.metrics.${name}${count === 1 ? 'One' : 'Other'}`
  return t(key, source === undefined ? { count } : { count, source })
}

/** Translates one `key=value` pair from a `partial_failure` error into a readable sentence. */
function translateMetricItem(
  rawKey: string,
  rawValue: string,
  t: Translate,
  sourceLabels: SourceLabels,
): string | null {
  const count = Number(rawValue)
  if (!Number.isFinite(count)) return null

  const fixedName = FIXED_METRIC_KEYS[rawKey]
  if (fixedName) return metricText(t, fixedName, count)

  const slash = rawKey.lastIndexOf('/')
  if (slash > 0) {
    const sourceName = rawKey.slice(0, slash)
    const suffix = rawKey.slice(slash + 1)
    const metricName = SOURCE_METRIC_SUFFIXES[suffix]
    if (metricName) {
      const source = resolveSourceLabel(sourceLabels, sourceName)
      return metricText(t, metricName, count, source)
    }
  }

  return metricText(t, 'other', count)
}

/**
 * Parses the ingestion service's `partial_failure: key=value, key=value, ...` error shape
 * (see `_run_error` in `ingestion/src/argus_ingestion/repository.py`) into a list of
 * human-readable, translated sentences. Returns `[]` if nothing in it could be parsed.
 */
function parsePartialFailure(body: string, t: Translate, sourceLabels: SourceLabels): string[] {
  const items: string[] = []
  for (const pair of body.split(',')) {
    const eq = pair.indexOf('=')
    if (eq < 0) continue
    const key = pair.slice(0, eq).trim()
    const value = pair.slice(eq + 1).trim()
    const text = translateMetricItem(key, value, t, sourceLabels)
    if (text) items.push(text)
  }
  return items
}

/**
 * Turns a raw `retrieval_log.error` string (as stored by ingestion and passed through
 * unmodified by the backend — see `RetrievalRun.error`) into a short, translated display
 * string plus a longer translated + raw string for a `title` tooltip. Returns `null` for
 * runs without an error, mirroring the raw value's own null-ness.
 *
 * Unrecognized shapes (an unknown finish reason, or free-text exception text from
 * `record_failed_run`) fall back to a generic translated message — the raw value is never
 * shown as the primary text, only in the tooltip.
 */
export function formatRunError(
  raw: string | null | undefined,
  t: Translate,
  sourceLabels: SourceLabels,
): RunErrorDisplay | null {
  if (!raw) return null

  const technicalDetails = t('errors.ingestionRun.technicalDetails', { raw })

  const partialFailureMatch = /^partial_failure:\s*(.+)$/.exec(raw)
  if (partialFailureMatch) {
    const items = parsePartialFailure(partialFailureMatch[1] ?? '', t, sourceLabels)
    if (items.length > 0) {
      const prefix = t('errors.ingestionRun.partialFailurePrefix')
      const extra = items.length - 1
      const text =
        extra > 0
          ? `${prefix}: ${items[0]} ${t('errors.ingestionRun.andMore', { count: extra })}`
          : `${prefix}: ${items[0]}`
      const title =
        items.length > 1
          ? `${prefix}: ${items.join('; ')} — ${technicalDetails}`
          : `${prefix}: ${items[0]} — ${technicalDetails}`
      return { text, title }
    }
  }

  const reasonKey = KNOWN_FINISH_REASONS[raw]
  const text = reasonKey
    ? t(`errors.ingestionRun.reasons.${reasonKey}`)
    : t('errors.ingestionRun.generic')
  return { text, title: `${text} — ${technicalDetails}` }
}
